import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { listMyPosts } from '../lib/feed';
import { heldPrints, markOpened, type Print as HeldPrint } from '../lib/reveal';
import { PrintOnPeg, PrintReveal } from '../components/Reveal';
import { track } from '../analytics';
import { transition } from '../lib/transition';
import { markPosted } from '../lib/feed-flash';
import { useViewed } from '../lib/viewed';

/**
 * /print/:postId — turning over a print that dried while you were away (from
 * Home or your feed): the same peg and the same turn as the reward, then back
 * to Home, where the picture now is. Every dry print in a row, the tapped post's first.
 * Opened from the road, it goes back to the road, where the stop pops as its picture lands.
 */
export function Print() {
  useViewed('print');
  const { postId } = useParams();
  const nav = useNavigate();
  const from = useLocation().state as { back?: string; landed?: string } | null;
  const leave = () => nav(from?.back ?? '/', { replace: true, viewTransition: true, state: from?.landed ? { landed: from.landed } : undefined });
  const [prints, setPrints] = useState<HeldPrint[] | null>(null);
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    listMyPosts(40)
      .then((posts) => {
        // every print that is dry, the one you tapped first: three dry means three turns, not one and back to Home
        const all = heldPrints(posts);
        setPrints([...all.filter((p) => p.postId === postId), ...all.filter((p) => p.postId !== postId)]);
      })
      .catch(() => setPrints([]));
  }, [postId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (prints && prints.length === 0) leave(); }, [prints]);

  const p = prints?.[i];
  if (!p) return <div className="stage" style={{ background: '#2b2a44' }} />;

  if (!open) {
    return (
      <PrintOnPeg
        day={p.day}
        words={p.idea}
        count={prints!.length - i}
        onTurn={() => { track('print_turned', { from: 'print', index: i }); void markOpened(p.postId, p.url); setOpen(true); }}
        onLater={leave}
      />
    );
  }
  return (
    <PrintReveal
      day={p.day}
      url={p.url}
      words={p.idea}
      onPost={() => markPosted(p.url)}
      onDone={() => {
        if (i + 1 < prints!.length) { transition(() => { setI(i + 1); setOpen(false); }); return; }
        leave();
      }}
    />
  );
}
